/**
 * Customize columns drawer — shared by the list pages (Submission Triage
 * OR-979, Policies OR-1664).
 *
 * Right-side drawer to show/hide and reorder table columns. Columns are
 * grouped; each group has a "Show all / Hide all" toggle and per-column
 * toggles, plus a drag handle to reorder within a group. A category strip
 * filters the list to one group; a search box filters by label. Changes
 * are staged locally and committed on Save (Cancel discards, Reset to
 * default restores the page's default visible set and natural order).
 *
 * The page owns its column metadata (`columns`, `groupOrder`,
 * `groupLabels`, the defaults); this component only knows how to lay a
 * column set out. Grouping uses each column's `customizeGroup` (falling
 * back to `group`) so a page can file a column under a different section
 * here than the band it sits in on the table.
 */
import { useMemo, useState } from "react";
import { Dialog as AriaDialog, Modal as AriaModal, ModalOverlay as AriaModalOverlay } from "react-aria-components";
import { DotsGrid, RefreshCcw01, SearchLg, X } from "@untitledui/icons";
import { Button, Input, Toggle, cx } from "@tricura/atlas-ui";

export interface CustomizableColumn<G extends string = string> {
    id: string;
    label: string;
    group: G;
    /** Section to list the column under here, when it differs from the
     *  table band it belongs to. */
    customizeGroup?: G;
}

export interface CustomizeApply {
    visibleIds: string[];
    order: string[];
}

export interface CustomizeColumnsDrawerProps<G extends string> {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    /** Every column the table can show, in its natural order. */
    columns: CustomizableColumn<G>[];
    /** Order the sections render in. Sections with no columns are skipped. */
    groupOrder: readonly G[];
    groupLabels: Record<G, string>;
    /** Currently committed visible column ids. */
    value: string[];
    /** Currently committed column order ids. */
    order: string[];
    /** What "Reset to default" restores. */
    defaultVisibleIds: string[];
    defaultOrder: string[];
    /** Columns that are always visible and cannot be moved (row identity). */
    lockedIds?: readonly string[];
    /** Prefix for the `data-autotest` hooks, e.g. `triage` → `triage-customize-…`. */
    autotestPrefix: string;
    /** Commit a new visible set + order. */
    onApply: (next: CustomizeApply) => void;
}

export function CustomizeColumnsDrawer<G extends string>({ isOpen, onOpenChange, onApply, ...body }: CustomizeColumnsDrawerProps<G>) {
    return (
        <AriaModalOverlay
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            isDismissable
            className={({ isEntering, isExiting }) =>
                cx(
                    "bg-overlay/70 fixed inset-0 z-50",
                    isEntering && "animate-in fade-in ease-out",
                    isExiting && "animate-out fade-out ease-in",
                )
            }
        >
            <AriaModal
                className={({ isEntering, isExiting }) =>
                    cx(
                        "bg-primary outline-hidden fixed bottom-0 right-0 top-0 w-[460px] max-w-full shadow-2xl",
                        isEntering && "animate-in slide-in-from-right ease-out",
                        isExiting && "animate-out slide-out-to-right ease-in",
                    )
                }
            >
                <AriaDialog aria-label="Customize" className="outline-hidden flex h-full flex-col">
                    {({ close }) => (
                        <CustomizeBody
                            {...body}
                            close={close}
                            onApply={(next) => {
                                onApply(next);
                                close();
                            }}
                        />
                    )}
                </AriaDialog>
            </AriaModal>
        </AriaModalOverlay>
    );
}

type BodyProps<G extends string> = Omit<CustomizeColumnsDrawerProps<G>, "isOpen" | "onOpenChange"> & { close: () => void };

/** The section a column is listed under here. */
function cgOf<G extends string>(c: CustomizableColumn<G>): G {
    return c.customizeGroup ?? c.group;
}

function CustomizeBody<G extends string>({
    columns,
    groupOrder,
    groupLabels,
    value,
    order,
    defaultVisibleIds,
    defaultOrder,
    lockedIds = [],
    autotestPrefix,
    close,
    onApply,
}: BodyProps<G>) {
    const [draft, setDraft] = useState<Set<string>>(() => new Set(value));
    const [draftOrder, setDraftOrder] = useState<string[]>(order);
    const [tab, setTab] = useState<"all" | G>("all");
    const [search, setSearch] = useState("");
    /** Column id currently being dragged (drag-to-reorder). */
    const [draggingId, setDraggingId] = useState<string | null>(null);

    // Only show category buttons for groups that actually have columns.
    const presentGroups = useMemo(() => groupOrder.filter((g) => columns.some((c) => cgOf(c) === g)), [columns, groupOrder]);
    const categoryTabs = useMemo<{ id: "all" | G; label: string }[]>(
        () => [{ id: "all", label: "All" }, ...presentGroups.map((g) => ({ id: g, label: groupLabels[g] }))],
        [presentGroups, groupLabels],
    );

    const groupOf = useMemo(() => new Map(columns.map((c) => [c.id, cgOf(c)])), [columns]);
    const orderRank = useMemo(() => new Map(draftOrder.map((id, i) => [id, i])), [draftOrder]);
    const locked = useMemo(() => new Set(lockedIds), [lockedIds]);
    const isLocked = (id: string) => locked.has(id);

    // Move `dragId` to `overId`'s slot — only within the same group, so the
    // table's group bands stay intact.
    const reorder = (dragId: string, overId: string) => {
        if (dragId === overId || isLocked(dragId) || isLocked(overId)) return;
        if (groupOf.get(dragId) !== groupOf.get(overId)) return;
        setDraftOrder((prev) => {
            const next = [...prev];
            const from = next.indexOf(dragId);
            const to = next.indexOf(overId);
            if (from < 0 || to < 0) return prev;
            next.splice(from, 1);
            next.splice(to, 0, dragId);
            return next;
        });
    };

    const toggle = (id: string) => {
        if (isLocked(id)) return;
        setDraft((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const setGroupAll = (group: G, on: boolean) => {
        setDraft((prev) => {
            const next = new Set(prev);
            for (const c of columns) {
                if (cgOf(c) !== group || isLocked(c.id)) continue;
                if (on) next.add(c.id);
                else next.delete(c.id);
            }
            return next;
        });
    };

    const groupsToShow = tab === "all" ? presentGroups : [tab];
    const term = search.trim().toLowerCase();

    return (
        <>
            <div className="border-secondary relative flex flex-col gap-0.5 border-b px-6 py-5">
                <h2 className="text-primary text-base font-semibold leading-6">Customize</h2>
                <p className="text-tertiary text-sm leading-5">Customize column visibility and order.</p>
                <button
                    type="button"
                    onClick={close}
                    aria-label="Close"
                    className="text-tertiary hover:text-primary size-8 hover:bg-secondary absolute right-3 top-3 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg"
                >
                    <X aria-hidden="true" className="size-5 stroke-[2.25px]" />
                </button>
            </div>

            <div className="border-secondary flex flex-col gap-3 border-b px-6 py-4">
                <Input
                    size="sm"
                    icon={SearchLg}
                    placeholder="Search"
                    value={search}
                    onChange={setSearch}
                    aria-label="Search columns"
                    data-autotest={`${autotestPrefix}-customize-input-search`}
                />
                {/* Individual category buttons (not a grouped segmented
                  * control) — atlas Button so the radius/style match the
                  * Cancel/Save buttons exactly. Size xs per the Policies
                  * design (Andressa), which Triage follows too. */}
                <div className="flex flex-wrap gap-2">
                    {categoryTabs.map((t) => (
                        <Button
                            key={t.id}
                            size="xs"
                            color={tab === t.id ? "primary" : "secondary"}
                            onClick={() => setTab(t.id)}
                            data-autotest={`${autotestPrefix}-customize-button-filter-category-${t.id}`}
                        >
                            {t.label}
                        </Button>
                    ))}
                </div>
            </div>

            <div className="atlas-scrollbar min-h-0 flex-1 overflow-y-auto px-6 py-4">
                {groupsToShow.map((group) => {
                    const cols = columns
                        .filter((c) => cgOf(c) === group && (term === "" || c.label.toLowerCase().includes(term)))
                        .sort((a, b) => (orderRank.get(a.id) ?? 0) - (orderRank.get(b.id) ?? 0));
                    if (cols.length === 0) return null;
                    const toggleable = cols.filter((c) => !isLocked(c.id));
                    const allOn = toggleable.length > 0 && toggleable.every((c) => draft.has(c.id));

                    return (
                        <section key={group} className="mb-5 flex flex-col gap-2">
                            {/* Same horizontal inset as the rows below (border +
                              * px-3) so the section toggle lines up with the
                              * column toggles — Andressa's note on the Policies
                              * Customize frame. */}
                            <div className="flex items-center justify-between px-[13px]">
                                <h3 className="text-primary text-sm font-semibold">{groupLabels[group]}</h3>
                                <label className="text-tertiary flex cursor-pointer items-center gap-2 text-xs">
                                    {allOn ? "Hide all" : "Show all"}
                                    <Toggle
                                        size="sm"
                                        isSelected={allOn}
                                        onChange={(on) => setGroupAll(group, on)}
                                        data-autotest={`${autotestPrefix}-customize-toggle-show-all-${group}`}
                                        aria-label={`Toggle all ${groupLabels[group]}`}
                                    />
                                </label>
                            </div>
                            {cols.map((c) => {
                                const lockedCol = isLocked(c.id);
                                // Reordering by drag is disabled while searching (the
                                // filtered list isn't the real order).
                                const canDrag = !lockedCol && term === "";
                                return (
                                    <div
                                        key={c.id}
                                        draggable={canDrag}
                                        onDragStart={
                                            canDrag
                                                ? (e) => {
                                                      setDraggingId(c.id);
                                                      e.dataTransfer.effectAllowed = "move";
                                                  }
                                                : undefined
                                        }
                                        onDragOver={
                                            canDrag
                                                ? (e) => {
                                                      e.preventDefault();
                                                      e.dataTransfer.dropEffect = "move";
                                                  }
                                                : undefined
                                        }
                                        onDrop={
                                            canDrag
                                                ? (e) => {
                                                      e.preventDefault();
                                                      if (draggingId) reorder(draggingId, c.id);
                                                      setDraggingId(null);
                                                  }
                                                : undefined
                                        }
                                        onDragEnd={() => setDraggingId(null)}
                                        className={cx(
                                            "border-secondary bg-primary flex items-center gap-2 rounded-lg border px-3 py-2.5",
                                            draggingId === c.id && "opacity-50",
                                        )}
                                    >
                                        {canDrag ? (
                                            <DotsGrid
                                                aria-hidden="true"
                                                className="text-fg-quaternary size-4 shrink-0 cursor-grab active:cursor-grabbing"
                                            />
                                        ) : (
                                            <span aria-hidden="true" className="size-4 shrink-0" />
                                        )}
                                        <span className={cx("flex-1 text-sm", lockedCol ? "text-tertiary" : "text-secondary")}>{c.label}</span>
                                        <Toggle
                                            size="sm"
                                            isSelected={lockedCol ? true : draft.has(c.id)}
                                            isDisabled={lockedCol}
                                            onChange={() => toggle(c.id)}
                                            aria-label={c.label}
                                            data-autotest={`${autotestPrefix}-customize-toggle-column-${c.id}`}
                                        />
                                    </div>
                                );
                            })}
                        </section>
                    );
                })}
            </div>

            <div className="border-secondary bg-primary flex items-center justify-between gap-3 border-t px-6 py-4">
                <Button
                    color="link-gray"
                    size="sm"
                    iconLeading={RefreshCcw01}
                    data-autotest={`${autotestPrefix}-customize-button-reset-default`}
                    onClick={() => {
                        setDraft(new Set(defaultVisibleIds));
                        setDraftOrder(defaultOrder);
                    }}
                >
                    Reset to default
                </Button>
                <div className="flex items-center gap-2">
                    <Button color="secondary" size="sm" onClick={close} data-autotest={`${autotestPrefix}-customize-button-cancel`}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        size="sm"
                        onClick={() => onApply({ visibleIds: Array.from(draft), order: draftOrder })}
                        data-autotest={`${autotestPrefix}-customize-button-save`}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </>
    );
}
